import React from 'react';
import { Helmet } from 'react-helmet-async';
import { gql, useQuery } from '@apollo/client';

const GET_SEO_METADATA = gql`
  query GetSEOMetadata($pagePath: String!) {
    seoMetadata(pagePath: $pagePath) {
      id
      pagePath
      pageTitle
      metaTitle
      metaDescription
      metaKeywords
      canonicalUrl
      ogTitle
      ogDescription
      ogImage
      ogType
      twitterCard
      twitterTitle
      twitterDescription
      twitterImage
      linkedInImage
      linkedInTitle
      linkedInDescription
      pinterestImage
      pinterestTitle
      pinterestDescription
      robots
      structuredData
    }
  }
`;

interface SEOHeadProps {
  pagePath: string;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

/**
 * SEOHead Component
 *
 * Automatically fetches and injects SEO metadata for a given page path.
 * Uses react-helmet-async to manage <head> tags.
 *
 * Usage:
 * ```tsx
 * <SEOHead
 *   pagePath="/solar/calculator"
 *   fallbackTitle="Solar Calculator"
 *   fallbackDescription="Calculate your solar ROI"
 * />
 * ```
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  pagePath,
  fallbackTitle = 'One Group',
  fallbackDescription = 'One Group - Your trusted partner',
}) => {
  const { data, loading, error } = useQuery(GET_SEO_METADATA, {
    variables: { pagePath },
    fetchPolicy: 'cache-first', // Cache for performance
  });

  // Use fetched SEO data or fallbacks
  const seo = data?.seoMetadata;

  const title = seo?.metaTitle || fallbackTitle;
  const description = seo?.metaDescription || fallbackDescription;
  const keywords = seo?.metaKeywords?.join(', ') || '';
  const canonicalUrl = seo?.canonicalUrl || `https://onegroup.com.au${pagePath}`;
  const robots = seo?.robots || 'index,follow';

  // Open Graph
  const ogTitle = seo?.ogTitle || title;
  const ogDescription = seo?.ogDescription || description;
  const ogImage = seo?.ogImage || 'https://onegroup.com.au/default-og-image.jpg';
  const ogType = seo?.ogType || 'website';

  // Twitter Card
  const twitterCard = seo?.twitterCard || 'summary_large_image';
  const twitterTitle = seo?.twitterTitle || ogTitle;
  const twitterDescription = seo?.twitterDescription || ogDescription;
  const twitterImage = seo?.twitterImage || ogImage;

  // LinkedIn (falls back to OG)
  const linkedInTitle = seo?.linkedInTitle || ogTitle;
  const linkedInImage = seo?.linkedInImage || ogImage;

  // Pinterest
  const pinterestImage = seo?.pinterestImage || ogImage;

  // Structured Data
  const structuredData = seo?.structuredData
    ? JSON.stringify(seo.structuredData)
    : null;

  // Don't render anything while loading to avoid flash of default content
  if (loading) return null;

  // Log error but still render fallback SEO
  if (error) {
    console.warn(`SEO metadata fetch error for ${pagePath}:`, error.message);
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph (Facebook, WhatsApp, LinkedIn fallback) */}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="One Group" />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={twitterImage} />

      {/* LinkedIn (uses OG by default, but can override) */}
      {seo?.linkedInImage && <meta property="og:image" content={linkedInImage} />}
      {seo?.linkedInTitle && <meta property="og:title" content={linkedInTitle} />}

      {/* Pinterest */}
      {seo?.pinterestImage && <meta name="pinterest:image" content={pinterestImage} />}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">{structuredData}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
